import blobUtil from 'blob-util';
import PropTypes from 'prop-types';
import React, { useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { jsPreprocess } from './jsPreprocess';
import { resolvePathToFile } from './filePath';
import { getConfig } from '../../utils/getConfig';
import { toOrigin } from '../../utils/toOrigin';
import {
  MEDIA_FILE_QUOTED_REGEX,
  STRING_REGEX,
  PLAINTEXT_FILE_REGEX,
  EXTERNAL_LINK_REGEX,
  NOT_EXTERNAL_LINK_REGEX
} from '../../utils/fileUtils';
import { getAllScriptOffsets } from '../../utils/consoleUtils';
import { registerFrame } from '../../utils/dispatcher';
import { createBlobUrl } from './filesReducer';
import resolvePathsForElementsWithAttribute from './resolveUtils';

let objectUrls = {};
let objectPaths = {};

const Frame = styled.iframe`
  min-height: 100%;
  min-width: 100%;
  position: absolute;
  border-width: 0;
  ${({ fullView }) =>
    fullView &&
    `
    position: relative;
  `}
`;

function getHtmlFile(files) {
  return files.filter((file) => file.name.match(/.*\.html$/i))[0];
}

function resolveCSSLinksInString(content, files) {
  let newContent = content;
  let cssFileStrings = content.match(STRING_REGEX);
  cssFileStrings = cssFileStrings || [];
  cssFileStrings.forEach((cssFileString) => {
    if (cssFileString.match(MEDIA_FILE_QUOTED_REGEX)) {
      const filePath = cssFileString.substr(1, cssFileString.length - 2);
      const quoteCharacter = cssFileString.substr(0, 1);
      const resolvedFile = resolvePathToFile(filePath, files);
      if (resolvedFile) {
        if (resolvedFile.url) {
          newContent = newContent.replace(
            cssFileString,
            quoteCharacter + resolvedFile.url + quoteCharacter
          );
        }
      }
    }
  });
  return newContent;
}

function resolveJSLinksInString(content, files) {
  const indexFile = getHtmlFile(files);
  const indexSrc = indexFile?.content;
  let newContent = content;
  let jsFileStrings = content.match(STRING_REGEX);
  jsFileStrings = jsFileStrings || [];
  jsFileStrings.forEach((jsFileString) => {
    if (jsFileString.match(MEDIA_FILE_QUOTED_REGEX)) {
      const filePath = jsFileString.substr(1, jsFileString.length - 2);
      const quoteCharacter = jsFileString.substr(0, 1);
      const resolvedFile = resolvePathToFile(filePath, files);

      if (resolvedFile) {
        if (resolvedFile.url) {
          newContent = newContent.replace(
            jsFileString,
            quoteCharacter + resolvedFile.url + quoteCharacter
          );
        } else if (resolvedFile.name.match(PLAINTEXT_FILE_REGEX)) {
          newContent = newContent.replace(
            jsFileString,
            quoteCharacter + resolvedFile.blobUrl + quoteCharacter
          );
        }
      }
    }
  });

  return jsPreprocess(newContent, indexSrc);
}

function resolveScripts(sketchDoc, files) {
  const scriptsInHTML = sketchDoc.getElementsByTagName('script');
  const scriptsInHTMLArray = Array.prototype.slice.call(scriptsInHTML);
  scriptsInHTMLArray.forEach((script) => {
    if (
      script.getAttribute('src') &&
      script.getAttribute('src').match(NOT_EXTERNAL_LINK_REGEX) !== null
    ) {
      const resolvedFile = resolvePathToFile(script.getAttribute('src'), files);
      if (resolvedFile) {
        if (resolvedFile.url) {
          script.setAttribute('src', resolvedFile.url);
        } else {
          // in the future, when using y.js, could remake the blob for only the file(s)
          // that changed
          const blobUrl = createBlobUrl(resolvedFile);
          script.setAttribute('src', blobUrl);
          const blobPath = blobUrl.split('/').pop();
          // objectUrls[blobUrl] = `${resolvedFile.filePath}${
          //   resolvedFile.filePath.length > 0 ? '/' : ''
          // }${resolvedFile.name}`;
          objectUrls[blobUrl] = `${resolvedFile.filePath}/${resolvedFile.name}`;
          objectPaths[blobPath] = resolvedFile.name;
          // script.setAttribute('data-tag', `${startTag}${resolvedFile.name}`);
          // script.removeAttribute('src');
          // script.innerHTML = resolvedFile.content; // eslint-disable-line
        }
      }
    } else if (
      !(
        script.getAttribute('src') &&
        script.getAttribute('src').match(EXTERNAL_LINK_REGEX)
      ) !== null
    ) {
      script.setAttribute('crossorigin', '');
      script.innerHTML = resolveJSLinksInString(script.innerHTML, files); // eslint-disable-line
    }
  });
}

function resolveStyles(sketchDoc, files) {
  const inlineCSSInHTML = sketchDoc.getElementsByTagName('style');
  const inlineCSSInHTMLArray = Array.prototype.slice.call(inlineCSSInHTML);
  inlineCSSInHTMLArray.forEach((style) => {
    style.innerHTML = resolveCSSLinksInString(style.innerHTML, files); // eslint-disable-line
  });

  const cssLinksInHTML = sketchDoc.querySelectorAll('link[rel="stylesheet"]');
  const cssLinksInHTMLArray = Array.prototype.slice.call(cssLinksInHTML);
  cssLinksInHTMLArray.forEach((css) => {
    if (
      css.getAttribute('href') &&
      css.getAttribute('href').match(NOT_EXTERNAL_LINK_REGEX) !== null
    ) {
      const resolvedFile = resolvePathToFile(css.getAttribute('href'), files);
      if (resolvedFile) {
        if (resolvedFile.url) {
          css.href = resolvedFile.url; // eslint-disable-line
        } else {
          const style = sketchDoc.createElement('style');
          style.innerHTML = `\n${resolvedFile.content}`;
          sketchDoc.head.appendChild(style);
          css.parentElement.removeChild(css);
        }
      }
    }
  });
}

function resolveJSAndCSSLinks(files) {
  const newFiles = [];
  files.forEach((file) => {
    const newFile = { ...file };
    if (file.name.match(/.*\.js$/i)) {
      newFile.content = resolveJSLinksInString(newFile.content, files);
    } else if (file.name.match(/.*\.css$/i)) {
      newFile.content = resolveCSSLinksInString(newFile.content, files);
    }
    newFiles.push(newFile);
  });
  return newFiles;
}

function addLoopProtect(sketchDoc, indexSrc) {
  const scriptsInHTML = sketchDoc.getElementsByTagName('script');
  const scriptsInHTMLArray = Array.prototype.slice.call(scriptsInHTML);
  scriptsInHTMLArray.forEach((script) => {
    script.innerHTML = jsPreprocess(script.innerHTML, indexSrc); // eslint-disable-line
  });
}

function injectLocalFiles(files, htmlFile, options) {
  const { basePath, gridOutput, textOutput, sliderValues } = options;
  let scriptOffs = [];
  objectUrls = {};
  objectPaths = {};
  const resolvedFiles = resolveJSAndCSSLinks(files);
  const parser = new DOMParser();
  const sketchDoc = parser.parseFromString(htmlFile.content, 'text/html');
  const indexFile = getHtmlFile(files);
  const indexSrc = indexFile?.content;

  const base = sketchDoc.createElement('base');
  base.href = `${window.origin}${basePath}${basePath.length > 1 && '/'}`;
  sketchDoc.head.appendChild(base);

  resolvePathsForElementsWithAttribute('src', sketchDoc, resolvedFiles);
  resolvePathsForElementsWithAttribute('href', sketchDoc, resolvedFiles);
  // should also include background, data, poster, but these are used way less often

  resolveScripts(sketchDoc, resolvedFiles);
  resolveStyles(sketchDoc, resolvedFiles);

  if (textOutput || gridOutput) {
    const scriptElement = sketchDoc.createElement('script');
    let textCode = '';
    if (textOutput) {
      textCode = 'if (!this._accessibleOutputs.text) this.textOutput();';
    }
    let gridCode = '';
    if (gridOutput) {
      gridCode = 'if (!this._accessibleOutputs.grid) this.gridOutput();';
    }
    const fxn = `p5.prototype.ensureAccessibleCanvas = function _ensureAccessibleCanvas() {
  ${textCode}
  ${gridCode}
};
p5.prototype.registerMethod('afterSetup', p5.prototype.ensureAccessibleCanvas);`;
    scriptElement.innerHTML = fxn;
    sketchDoc.head.appendChild(scriptElement);
  }

  const previewScripts = sketchDoc.createElement('script');
  // PREVIEW_SCRIPTS_URL may be an absolute URL (our deploy: served from the preview Hosting site) or a
  // path (prepend the current origin). Prepending the origin to an already-absolute URL would produce a
  // doubled, unresolvable src (…web.apphttps://…) and the preview would never load.
  const scriptsUrl = getConfig('PREVIEW_SCRIPTS_URL', { nullishString: true });
  previewScripts.src = /^https?:\/\//.test(scriptsUrl)
    ? scriptsUrl
    : `${window.location.origin}${scriptsUrl}`;
  previewScripts.setAttribute('crossorigin', '');
  sketchDoc.head.appendChild(previewScripts);

  const sketchDocString = `<!DOCTYPE HTML>\n${sketchDoc.documentElement.outerHTML}`;
  scriptOffs = getAllScriptOffsets(sketchDocString);
  const consoleErrorsScript = sketchDoc.createElement('script');
  consoleErrorsScript.innerHTML = `
    window.offs = ${JSON.stringify(scriptOffs)};
    window.objectUrls = ${JSON.stringify(objectUrls)};
    window.objectPaths = ${JSON.stringify(objectPaths)};
    window.editorOrigin = '${toOrigin(
      // Same dev fallback as previewIndex: editor and preview are same-origin in dev, so this
      // page's own origin IS the editor origin; EDITOR_URL is only set for production.
      getConfig('EDITOR_URL', { warn: false }) ?? window.origin
    )}';
    // Stored AR slider values. This script is prepended to <head>, so it runs BEFORE the
    // arSlider.js helper and the sketch — letting the helper seed its registry's _persist
    // from these, so createArSlider() in setup() resolves to the stored value instead of
    // the code default (persist-by-default). Empty {} when no AR session is active.
    window.__arSliderInitialValues = ${JSON.stringify(sliderValues || {})};
  `;
  addLoopProtect(sketchDoc, indexSrc);
  sketchDoc.head.prepend(consoleErrorsScript);

  return `<!DOCTYPE HTML>\n${sketchDoc.documentElement.outerHTML}`;
}

function EmbedFrame({
  files,
  isPlaying,
  basePath,
  gridOutput,
  textOutput,
  sliderValues
}) {
  const iframe = useRef();
  const htmlFile = useMemo(() => getHtmlFile(files), [files]);
  const srcRef = useRef();

  useEffect(() => {
    const unsubscribe = registerFrame(
      iframe.current.contentWindow,
      window.origin
    );
    return () => {
      unsubscribe();
    };
  });

  function renderSketch() {
    const doc = iframe.current;
    if (isPlaying) {
      const htmlDoc = injectLocalFiles(files, htmlFile, {
        basePath,
        gridOutput,
        textOutput,
        sliderValues
      });
      const generatedHtmlFile = {
        name: 'index.html',
        content: htmlDoc
      };
      const htmlUrl = createBlobUrl(generatedHtmlFile);
      const toRevoke = srcRef.current;
      srcRef.current = htmlUrl;
      // BRO FOR SOME REASON YOU HAVE TO DO THIS TO GET IT TO WORK ON SAFARI
      setTimeout(() => {
        doc.src = htmlUrl;
        if (toRevoke) {
          blobUtil.revokeObjectURL(toRevoke);
        }
      }, 0);
    } else {
      doc.src = '';
    }
  }

  useEffect(renderSketch, [files, isPlaying]);
  return (
    <Frame
      aria-label="Sketch Preview"
      role="main"
      frameBorder="0"
      ref={iframe}
    />
  );
}

EmbedFrame.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired
    })
  ).isRequired,
  isPlaying: PropTypes.bool.isRequired,
  basePath: PropTypes.string.isRequired,
  gridOutput: PropTypes.bool.isRequired,
  textOutput: PropTypes.bool.isRequired,
  // Slider name -> value map. Optional: absent/empty when no AR session is active.
  sliderValues: PropTypes.objectOf(PropTypes.number)
};

EmbedFrame.defaultProps = {
  sliderValues: {}
};

export default EmbedFrame;
